import { Link, useLocation } from "react-router-dom";
import { SectionEyebrow } from "./SectionEyebrow";
import { useLang } from "../../i18n/LanguageContext";

const LEGAL_ROUTES = ["/aviso-legal", "/privacidad", "/cookies", "/terminos-de-uso"];

const PENDING_RE = /\[\[([^\]]+)\]\]/g;

export const renderLegalText = (text, pendingWord = "pendiente", pendingTitle = "Campo pendiente de configuración") => {
  const parts = [];
  let last = 0;
  let match;
  let key = 0;
  while ((match = PENDING_RE.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <span key={key++} className="pending-field" title={pendingTitle}>
        {match[1]} · {pendingWord}
      </span>
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const collectPending = (sections) => {
  const found = new Set();
  const scan = (t) => {
    let m;
    const re = /\[\[([^\]]+)\]\]/g;
    while ((m = re.exec(t)) !== null) found.add(m[1]);
  };
  sections.forEach((s) => {
    (s.paragraphs || []).forEach(scan);
    (s.paragraphsAfter || []).forEach(scan);
    (s.list || []).forEach(scan);
  });
  return [...found];
};

export const LegalLayout = ({ page }) => {
  const location = useLocation();
  const { t } = useLang();
  const tl = t.legal;
  const pending = collectPending(page.sections);
  const isDev = process.env.NODE_ENV === "development";
  const render = (text) => renderLegalText(text, tl.pendingWord, tl.pendingTitle);

  return (
    <main data-testid="legal-layout" className="pt-[76px]">
      <div className="onix-shell py-20 md:py-28">
        <SectionEyebrow index="§" label={tl.sectionLabel} />
        <div className="grid lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-3 min-w-0">
            <nav className="lg:sticky lg:top-28 space-y-1" aria-label={tl.pagesNav}>
              {LEGAL_ROUTES.map((to) => (
                <Link
                  key={to}
                  data-testid={`legal-nav-${to.replace("/", "")}`}
                  to={to}
                  className={`block border-l py-2.5 pl-5 text-xs font-bold uppercase tracking-[0.12em] transition-colors ${
                    location.pathname === to
                      ? "border-onix-white text-onix-white"
                      : "border-onix-line text-onix-gray hover:text-onix-silver"
                  }`}
                >
                  {tl.navLabels[to.replace("/", "")]}
                </Link>
              ))}
            </nav>
          </aside>
          <article className="lg:col-span-8 legal-prose min-w-0">
            <h1 className="text-4xl sm:text-5xl font-bold font-serif text-onix-white mb-6">{page.title}</h1>
            <p className="text-sm text-onix-gray mb-4">{tl.ownershipLine}</p>
            {isDev && pending.length > 0 && (
              <div
                data-testid="legal-admin-alert"
                className="border border-dashed border-onix-silver p-5 mb-8 text-xs text-onix-silver"
                role="note"
              >
                <p className="eyebrow mb-2">{tl.adminTitle}</p>
                <p>{tl.adminNote}</p>
                <p className="mt-2 text-onix-gray">
                  {tl.adminFields} {pending.join(" · ")}
                </p>
              </div>
            )}
            {page.sections.map((s, i) => (
              <section key={i} id={`seccion-${i + 1}`}>
                <h2>{s.heading}</h2>
                {(s.paragraphs || []).map((p, j) => (
                  <p key={j}>{render(p)}</p>
                ))}
                {s.list && (
                  <ul>
                    {s.list.map((li, j) => (
                      <li key={j}>{render(li)}</li>
                    ))}
                  </ul>
                )}
                {s.table && (
                  <div className="overflow-x-auto my-6">
                    <table className="w-full text-left text-sm border border-onix-line">
                      <thead>
                        <tr>
                          {s.table.headers.map((h) => (
                            <th key={h} className="border-b border-onix-line px-4 py-3 text-[11px] uppercase tracking-[0.1em] text-onix-white font-bold">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {s.table.rows.map((row, r) => (
                          <tr key={r}>
                            {row.map((cell, c) => (
                              <td key={c} className="px-4 py-3 text-onix-silver align-top">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {(s.paragraphsAfter || []).map((p, j) => (
                  <p key={j}>{render(p)}</p>
                ))}
              </section>
            ))}
          </article>
        </div>
      </div>
    </main>
  );
};
